// Feature: address-geolocation, Task 8.1 + 8.4
//
// AddressForm — shared 7-field structured Address editor.
//
// Contract (Req 12.1, Req 2.2):
//   - Fields: organisation, addressLine1, addressLine2, locality, town,
//     county, postcode, countryCode.
//   - Required (asterisk + inline error on blur/submit): addressLine1, town,
//     postcode, countryCode.
//   - Read-only single-line preview via `formatAddress` (derived — never
//     stored as canonical, Req 2.3 / 2.4).
//   - Controlled component: parent owns state; passes `value` + `onChange`.
//   - Design-language tokens: card radius 12, control radius 7–8, required
//     label with `*`, error text in red-600 (design-language.md §Buttons +
//     §Typography, frontend-standards.md §Validation).
//
// Non-goals for this component:
//   - Map / pin drag — that is the `Map_Component` (task 8.2), out of scope
//     for the buildings unblock.
//   - Postcode geocoding on save — that is a wiring concern of the owning
//     form (e.g. buildings Task 5.6). This form emits changes; the parent
//     decides whether to geocode.
//   - Backfill or migration — those live in the resource Lambdas.

import React, { useMemo } from "react";
import { Address, formatAddress } from "@akfdev-com/shared";

/**
 * A partially-filled Address as edited by a user. All fields are optional
 * strings while editing; the owning form applies required-field validation
 * before persisting a full `Address`.
 */
export type PartialAddress = Partial<Address>;

export interface AddressFormErrors {
  addressLine1?: string;
  town?: string;
  postcode?: string;
  countryCode?: string;
}

export interface AddressFormProps {
  /** Current editor state. Empty object means a fresh, empty form. */
  value: PartialAddress;
  /** Called with the next state on every keystroke. Immutable update. */
  onChange: (next: PartialAddress) => void;
  /**
   * Optional per-field validation errors from the parent (usually surfaced
   * on submit). Rendered inline below the field per frontend-standards.md.
   */
  errors?: AddressFormErrors;
  /** Disable every input (e.g. while the parent is saving). */
  disabled?: boolean;
  /**
   * Optional visible ID prefix. Multiple `AddressForm`s on a page should
   * pass a unique prefix (e.g. `"building-settings"`) so `<label htmlFor>`
   * remains valid and screen readers navigate cleanly.
   */
  idPrefix?: string;
}

const REQUIRED = new Set<keyof Address>([
  "addressLine1",
  "town",
  "postcode",
  "countryCode",
]);

/**
 * Guarantee we have a full-enough `Address` to feed `formatAddress` without
 * lying about optional fields. We treat missing required fields as empty
 * strings — the preview then reads as "as much as I've typed so far".
 */
function toAddressForPreview(value: PartialAddress): Address {
  return {
    organisation: value.organisation,
    addressLine1: value.addressLine1 ?? "",
    addressLine2: value.addressLine2,
    locality: value.locality,
    town: value.town ?? "",
    county: value.county,
    postcode: value.postcode ?? "",
    countryCode: value.countryCode ?? "",
  };
}

const AddressForm: React.FC<AddressFormProps> = ({
  value,
  onChange,
  errors = {},
  disabled = false,
  idPrefix = "address",
}) => {
  const preview = useMemo(
    () => formatAddress(toAddressForPreview(value)),
    [value],
  );

  const handleField =
    (field: keyof Address) =>
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      const raw = e.target.value;
      // Preserve empty-string as `undefined` for optional fields to match
      // the stored-model convention (optional fields are `undefined`, not `""`).
      const isOptional = !REQUIRED.has(field);
      const next: PartialAddress = { ...value };
      if (isOptional && raw.length === 0) {
        delete next[field];
      } else {
        next[field] = raw;
      }
      onChange(next);
    };

  const fieldClass =
    "block w-full rounded-md border border-gray-300 shadow-sm " +
    "focus:border-primary-500 focus:ring-primary-500 sm:text-sm " +
    "px-3 py-2 disabled:bg-gray-50 disabled:cursor-not-allowed";
  const errorFieldClass = fieldClass.replace(
    "border-gray-300",
    "border-red-500",
  );
  const labelClass =
    "block text-xs font-medium text-gray-600 mb-1 uppercase tracking-wide";
  const requiredMark = <span className="text-red-600 ml-0.5">*</span>;

  const renderField = (
    field: keyof Address,
    label: string,
    autoComplete: string,
    opts?: { placeholder?: string; maxLength?: number },
  ): JSX.Element => {
    const isRequired = REQUIRED.has(field);
    const inputId = `${idPrefix}-${field}`;
    const errorId = `${inputId}-error`;
    const error = errors[field as keyof AddressFormErrors];
    return (
      <div>
        <label htmlFor={inputId} className={labelClass}>
          {label}
          {isRequired ? requiredMark : null}
        </label>
        <input
          id={inputId}
          name={field}
          type="text"
          autoComplete={autoComplete}
          className={error ? errorFieldClass : fieldClass}
          value={value[field] ?? ""}
          onChange={handleField(field)}
          disabled={disabled}
          maxLength={opts?.maxLength}
          placeholder={opts?.placeholder}
          aria-required={isRequired || undefined}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
        />
        {error ? (
          <p id={errorId} className="text-red-600 text-xs mt-1">
            {error}
          </p>
        ) : null}
      </div>
    );
  };

  return (
    <fieldset
      className="space-y-4"
      aria-label="Postal address"
      disabled={disabled}
      data-testid="address-form"
    >
      <legend className="sr-only">Postal address</legend>

      {renderField("organisation", "Organisation", "organization")}

      {renderField("addressLine1", "Address line 1", "address-line1", {
        placeholder: "House / flat number and street",
      })}

      {renderField("addressLine2", "Address line 2", "address-line2")}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {renderField("locality", "Locality", "address-level3")}
        {renderField("town", "Town / city", "address-level2")}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {renderField("county", "County", "address-level1")}
        {renderField("postcode", "Postcode", "postal-code", { maxLength: 12 })}
      </div>

      {renderField("countryCode", "Country (ISO 3166-1 alpha-2)", "country", {
        placeholder: "GB",
        maxLength: 2,
      })}

      <div>
        <p className={labelClass}>Preview</p>
        <p
          className="rounded-md bg-gray-50 border border-gray-200 px-3 py-2 text-sm text-gray-700 whitespace-pre-wrap"
          data-testid="address-preview"
          aria-live="polite"
        >
          {preview.length > 0 ? preview : "—"}
        </p>
      </div>
    </fieldset>
  );
};

export default AddressForm;
